import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Close as X } from '@mui/icons-material';

const NumberInputModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  description, 
  contentType, // 'flashcards' or 'quiz'
  defaultValue = 25,
  minValue = 5,
  maxValue = 25
}) => {
  const [numItems, setNumItems] = useState(defaultValue);

  const handleConfirm = () => {
    onConfirm(numItems);
    onClose();
  };

  const handleCancel = () => {
    setNumItems(defaultValue);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="modal-content bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-200/50 dark:border-gray-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Clean Header */}
          <div className="modal-header bg-gradient-to-r from-teal-500 to-blue-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">{title}</h2>
                <p className="text-sm text-teal-100 mt-1">{description}</p>
              </div>
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Simplified Content */}
          <div className="modal-body p-6">
            <div className="space-y-4">
              <div className="text-center">
                <label htmlFor="numItems" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  How many {contentType}?
                </label>
                <div className="relative max-w-xs mx-auto">
                  <input
                    id="numItems"
                    type="number"
                    min={minValue}
                    max={maxValue}
                    value={numItems}
                    onChange={(e) => setNumItems(parseInt(e.target.value) || defaultValue)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-xl font-semibold transition-all duration-200"
                    placeholder={`${minValue}-${maxValue}`}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-xs mx-auto">
                  <span>Min: {minValue}</span>
                  <span>Max: {maxValue}</span>
                </div>
              </div>
            </div>

            {/* Clean Actions */}
            <div className="modal-footer flex gap-3 mt-6">
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={numItems < minValue || numItems > maxValue}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-xl font-medium transition-all duration-200 disabled:cursor-not-allowed shadow-lg"
              >
                Generate
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NumberInputModal; 